import { useApolloClient } from "@apollo/client";
import {
  CardContainer,
  DataGrid,
  GridColDef,
  InfoLabelMessage,
  ModalOpenButton,
} from "@msys/ui";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { IconButton, Stack, Switch, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { ConfirmModal } from "../../../commons/modals/ConfirmModal.js";
import { OrganisationWorkflowPanel } from "../modals/OrganisationWorkflowPanel.js";
import {
  OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment,
  useDeleteOrganisationWorkflowMutation,
  useSetOrganisationWorkflowActiveMutation,
} from "./OrganisationAutomatedWorkflowsBox.generated.js";

const WORKFLOW_TYPE_TITLES: Record<string, string> = {
  customerConfirmationWorkflow: "Send quote confirmation to customer",
  contractorNotificationWorkflow: "Notify contractor about new lead",
  customerQuotePdfWorkflow: "Send quote details with PDF to customer",
};

interface Props {
  workflows: OrganisationAutomatedWorkflowsBox_OrganisationWorkflowFragment[];
  emailTemplates: {
    id: string;
    description: string;
    context: string;
    attachments: { id: string }[];
  }[];
  organisationId?: string;
}

export function OrganisationAutomatedWorkflowsBox({
  workflows,
  emailTemplates,
  organisationId,
}: Props) {
  const { t } = useTranslate(["OrganisationSettings"]);

  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();

  const [setWorkflowActive] = useSetOrganisationWorkflowActiveMutation({
    client,
  });

  const [deleteWorkflow] = useDeleteOrganisationWorkflowMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationWorkflows,
      namedOperations.Query.OrganisationSettingsIntegrations,
    ],
  });

  const handleToggle = React.useCallback(
    async (workflowId: string, active: boolean) => {
      try {
        await setWorkflowActive({
          variables: { workflowId, active },
        });
      } catch (error: any) {
        enqueueSnackbar(error.message, { variant: "error" });
      }
    },
    [setWorkflowActive, enqueueSnackbar]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "workflowType",
        headerName: t("Type" as any, { ns: "OrganisationSettings" }),
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {!row.emailTemplateId && (
              <Tooltip
                title={t("Email template missing" as any, {
                  ns: "OrganisationSettings",
                })}
              >
                <WarningIcon color="warning" fontSize="small" />
              </Tooltip>
            )}
            <span>
              {t(
                (WORKFLOW_TYPE_TITLES[row.workflowType] ??
                  row.workflowType) as any,
                { ns: "OrganisationSettings" }
              )}
            </span>
          </Stack>
        ),
      },
      {
        field: "description",
        headerName: t("Description", { ns: "OrganisationSettings" }),
        flex: 1,
        sortable: false,
      },
      {
        field: "active",
        headerName: t("Active" as any, { ns: "OrganisationSettings" }),
        width: 80,
        sortable: false,
        renderCell: ({ row }) => (
          <Switch
            checked={row.active}
            onChange={(_e, checked) => handleToggle(row.id, checked)}
            size="small"
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 92,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <ModalOpenButton
              Modal={OrganisationWorkflowPanel}
              modalProps={{
                workflow: row,
                emailTemplates,
                organisationId,
              }}
            >
              <IconButton color="primary" size="small">
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
            <ModalOpenButton
              Modal={ConfirmModal}
              modalProps={{
                handleConfirm: async () => {
                  await deleteWorkflow({
                    variables: { workflowId: row.id },
                  });
                },
              }}
            >
              <IconButton color="primary" size="small">
                <DeleteIcon />
              </IconButton>
            </ModalOpenButton>
          </Stack>
        ),
      },
    ],
    [t, handleToggle, emailTemplates, organisationId, deleteWorkflow]
  );

  return (
    <CardContainer
      title={t("Automated workflows", {
        ns: "OrganisationSettings",
      })}
      isExpandable
      ActionButton={
        <ModalOpenButton
          Modal={OrganisationWorkflowPanel}
          modalProps={{
            emailTemplates,
            organisationId,
          }}
        >
          <IconButton color="primary">
            <AddIcon />
          </IconButton>
        </ModalOpenButton>
      }
    >
      <Stack padding={1} spacing={1}>
        <InfoLabelMessage
          message={t(
            "These are automated workflows which are triggered when a quote is created",
            { ns: "OrganisationSettings" }
          )}
        />
        <DataGrid
          columns={columns}
          rows={workflows}
          hideFooter
          paginationMode="client"
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          sx={{ "& .MuiDataGrid-row.MuiDataGrid-row": { cursor: "default" } }}
        />
      </Stack>
    </CardContainer>
  );
}
